import { useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, Check, Copy, Loader2, ShieldCheck } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import {
  duplicationErrorMessage,
  formatMinorBudget,
  parseDailyBudget,
  sourceBudgetPrefill,
} from "@/lib/ads-manager-duplication";
import type { DuplicateAdIdentity, DuplicateAdsPreflightResponse } from "@shared/ads-manager-duplication";
import type { DuplicationLaunch } from "@shared/ads-manager-duplication-engine";
import { clearPendingAdmission, savePendingAdmission, usePendingDuplicationAdmission } from "@/lib/ads-manager-duplication-admission";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

type SourceMetricAd = {
  id: string;
  name: string;
  accountId?: string;
  metrics?: { spend?: number | null };
};

type Props = {
  brandId: string;
  brandName: string;
  accountId: string;
  currency: string | null;
  ads: SourceMetricAd[];
  onRemoveBlocked: (ids: string[]) => void;
  onClose: () => void;
  onAdmitted: (duplicationId: string) => void;
};

function localDate() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

function money(value: number | null | undefined, currency: string | null) {
  if (value == null || !Number.isFinite(value)) return "Not available";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${currency ?? ""} ${value.toFixed(2)}`.trim();
  }
}

function responseError(error: unknown) {
  if (error instanceof Error) {
    const match = error.message.match(/^\d{3}:\s*(.*)$/s);
    if (match) {
      try {
        const parsed = JSON.parse(match[1]);
        if (typeof parsed?.error === "string") return parsed.error;
        if (typeof parsed?.message === "string") return parsed.message;
      } catch {
        return match[1] || duplicationErrorMessage(error);
      }
    }
  }
  return duplicationErrorMessage(error);
}

function httpStatus(error: unknown) {
  if (!(error instanceof Error)) return null;
  const match = error.message.match(/^(\d{3}):/);
  return match ? Number(match[1]) : null;
}

export function DuplicateAdsDialog({
  brandId,
  brandName,
  accountId,
  currency,
  ads,
  onRemoveBlocked,
  onClose,
  onAdmitted,
}: Props) {
  const admission = usePendingDuplicationAdmission();
  const priorAdmission = admission.pending?.brandId === brandId && admission.pending.payload.accountId === accountId
    && JSON.stringify(admission.pending.payload.adIds) === JSON.stringify(ads.map(ad => ad.id))
    ? admission.pending.payload : undefined;
  const [preflight, setPreflight] = useState<DuplicateAdsPreflightResponse | null>(null);
  const [preflightLoading, setPreflightLoading] = useState(true);
  const [preflightError, setPreflightError] = useState<string | null>(null);
  const [campaignName, setCampaignName] = useState(priorAdmission?.campaign.name ?? `${brandName} | Winners Test | ${localDate()}`);
  const [budgetMode, setBudgetMode] = useState<"abo" | "cbo">(priorAdmission?.campaign.budgetMode ?? "abo");
  const [structure, setStructure] = useState<"one_adset_per_ad" | "single_adset">(priorAdmission?.structure ?? "one_adset_per_ad");
  const [dailyBudget, setDailyBudget] = useState(priorAdmission && currency ? formatMinorBudget(priorAdmission.adsetDailyBudgetMinor ?? priorAdmission.campaign.cboDailyBudgetMinor!, currency) : "");
  const [suffix, setSuffix] = useState(priorAdmission?.adNameSuffix ?? " - Copy");
  const [launching, setLaunching] = useState(false);
  const [launchError, setLaunchError] = useState<string | null>(null);
  const [frozenPayload, setFrozenPayload] = useState<DuplicationLaunch | null>(priorAdmission ?? null);
  const [frozenAmbiguous, setFrozenAmbiguous] = useState(!!priorAdmission);
  const launchGuard = useRef(false);
  const requestKeys = useRef(new Map<string, string>());
  const autoPrefilledValue = useRef<string | null>(null);
  const mounted = useRef(true);
  const operationGeneration = useRef(0);

  useEffect(() => {
    mounted.current = true;
    operationGeneration.current += 1;
    return () => {
      mounted.current = false;
      launchGuard.current = false;
      operationGeneration.current += 1;
    };
  }, []);

  const selectedIds = useMemo(() => ads.map(ad => ad.id), [ads]);
  const selectedKey = selectedIds.join("\u0000");

  useEffect(() => {
    let active = true;
    setPreflight(null);
    setPreflightError(null);
    setPreflightLoading(true);
    apiRequest("POST", `/api/brands/${encodeURIComponent(brandId)}/ads-manager/duplicate-ads/preflight`, {
      accountId,
      adIds: selectedIds,
    }).then(async response => {
      const data = await response.json() as DuplicateAdsPreflightResponse;
      if (!active || !mounted.current) return;
      if (data.accountId !== accountId || !Array.isArray(data.ads)) {
        setPreflightError("Fresh source details did not match the selected ad account.");
        return;
      }
      const byId = new Map<string, DuplicateAdIdentity[]>();
      data.ads.forEach(row => byId.set(row.adId, [...(byId.get(row.adId) ?? []), row]));
      const identities = selectedIds.map(id => byId.get(id));
      if (identities.some(rows => !rows || rows.length !== 1) || data.ads.length !== selectedIds.length) {
        setPreflightError("Fresh source details did not match the selected ads. Refresh the selection and try again.");
        return;
      }
      setPreflight({ ...data, ads: identities.map(rows => rows![0]) });
    }).catch(error => {
      if (active && mounted.current) setPreflightError(responseError(error));
    }).finally(() => {
      if (active && mounted.current) setPreflightLoading(false);
    });
    return () => { active = false; };
    // The keyed parent remounts this dialog when selection or scope changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId, accountId, selectedKey]);

  const rows = useMemo(() => {
    if (!preflight) return [];
    const sourceById = new Map(ads.map(ad => [ad.id, ad]));
    return preflight.ads.map(identity => ({ identity, source: sourceById.get(identity.adId) }));
  }, [preflight, ads]);

  const blockedIds = rows.filter(row => row.identity.proofMode === "blocked").map(row => row.identity.adId);
  const eligibleRows = rows.filter(row => row.identity.proofMode !== "blocked");
  const firstSource = rows[0]?.identity;
  const currencyMatches = !!currency && !!rows.length && rows.every(({ identity }) => identity.source.currency === currency);
  const currencyWarning = !currency
    ? "The displayed account currency is unavailable. Refresh the account before entering a budget or submitting."
    : rows.some(({ identity }) => !identity.source.currency)
      ? "Fresh source currency was not returned by the reporting provider. Retry source check. Budgets cannot be submitted until the source currency is verified."
      : "Fresh source currency does not match the displayed account currency. Refresh the account before entering a budget or submitting.";
  const prefill = useMemo(
    () => preflight ? sourceBudgetPrefill(preflight.ads, currency) : { value: "", reason: null },
    [preflight, currency],
  );
  const budgetMinor = parseDailyBudget(dailyBudget, currency);
  const budgetInvalid = dailyBudget.trim() !== "" && budgetMinor === null;
  const hasBudget = budgetMinor !== null && budgetMinor > 0;
  const canSubmit = !!preflight && !preflightLoading && !preflightError && !blockedIds.length && currencyMatches && !admission.error
    && eligibleRows.length > 0 && campaignName.trim().length > 0 && !budgetInvalid && hasBudget
    && !(frozenAmbiguous && frozenPayload === null);
  const inputsLocked = frozenAmbiguous || launching;

  useEffect(() => {
    if (budgetMode !== "abo" || !prefill.value || dailyBudget) return;
    const parsed = parseDailyBudget(prefill.value, currency);
    if (parsed !== null && parsed > 0) {
      autoPrefilledValue.current = prefill.value;
      setDailyBudget(prefill.value);
    }
  }, [budgetMode, prefill.value, currency, dailyBudget]);

  async function refreshPreflight() {
    setPreflight(null);
    setPreflightError(null);
    setPreflightLoading(true);
    try {
      const response = await apiRequest("POST", `/api/brands/${encodeURIComponent(brandId)}/ads-manager/duplicate-ads/preflight`, {
        accountId,
        adIds: selectedIds,
      });
      const data = await response.json() as DuplicateAdsPreflightResponse;
      if (!mounted.current) return;
      if (data.accountId !== accountId || !Array.isArray(data.ads) || data.ads.length !== selectedIds.length) {
        setPreflightError("Fresh source details did not match the selected ads.");
        return;
      }
      const byId = new Map<string, DuplicateAdIdentity[]>();
      data.ads.forEach(row => byId.set(row.adId, [...(byId.get(row.adId) ?? []), row]));
      if (selectedIds.some(id => byId.get(id)?.length !== 1)) {
        setPreflightError("Fresh source details did not match the selected ads.");
        return;
      }
      setPreflight({ ...data, ads: selectedIds.map(id => byId.get(id)![0]) });
    } catch (error) {
      if (mounted.current) setPreflightError(responseError(error));
    } finally {
      if (mounted.current) setPreflightLoading(false);
    }
  }

  function createPayload(): DuplicationLaunch | null {
    if (!canSubmit || budgetMinor === null) return null;
    const payload: Omit<DuplicationLaunch, "requestKey"> = {
      accountId,
      adIds: selectedIds,
      campaign: {
        name: campaignName.trim(),
        budgetMode,
        ...(budgetMode === "cbo" ? { cboDailyBudgetMinor: budgetMinor } : {}),
      },
      structure,
      ...(budgetMode === "abo" ? { adsetDailyBudgetMinor: budgetMinor } : {}),
      adNameSuffix: suffix,
      live: true,
    };
    const key = JSON.stringify(payload);
    let requestKey = requestKeys.current.get(key);
    if (!requestKey) {
      requestKey = crypto.randomUUID();
      requestKeys.current.set(key, requestKey);
    }
    return { ...payload, requestKey };
  }

  async function submit() {
    if (launchGuard.current) return;
    const payload = frozenPayload ?? createPayload();
    if (!payload) return;
    if (!currencyMatches || admission.error) return;
    try {
      savePendingAdmission(admission.actorId, { brandId, currency: currency!, payload });
    } catch (error) {
      setLaunchError(responseError(error));
      return;
    }
    launchGuard.current = true;
    const generation = operationGeneration.current;
    setLaunching(true);
    setLaunchError(null);
    try {
      const response = await apiRequest("POST", `/api/brands/${encodeURIComponent(brandId)}/ads-manager/duplicate-ads/launch`, payload);
      const result = await response.json() as { duplicationId?: string };
      if (!mounted.current || generation !== operationGeneration.current) return;
      if (!result?.duplicationId) {
        setFrozenPayload(payload);
        setFrozenAmbiguous(true);
        setLaunchError("The server response did not confirm admission. Retry with the same request details, or check Ads Manager before starting anything else.");
        return;
      }
      onAdmitted(result.duplicationId);
      clearPendingAdmission(admission.actorId, payload.requestKey);
    } catch (error) {
      if (!mounted.current || generation !== operationGeneration.current) return;
      const status = httpStatus(error);
      setLaunchError(responseError(error));
      if (status !== 400 && status !== 422) {
        setFrozenPayload(payload);
        setFrozenAmbiguous(true);
      } else {
        clearPendingAdmission(admission.actorId, payload.requestKey);
        setFrozenPayload(null);
        setFrozenAmbiguous(false);
      }
    } finally {
      if (generation === operationGeneration.current) {
        launchGuard.current = false;
        if (mounted.current) setLaunching(false);
      }
    }
  }

  function onDialogOpenChange(open: boolean) {
    if (!open && !launching) onClose();
  }

  const reason = budgetMode === "abo"
    ? prefill.reason
    : "Campaign budget is required for CBO. Source ad-set budgets are not reused as campaign budgets.";

  return (
    <Dialog open onOpenChange={onDialogOpenChange}>
      <DialogContent className="max-h-[92dvh] max-w-4xl overflow-y-auto border-border/80 bg-background p-0 shadow-xl">
        <div className="border-b bg-muted/30 px-5 py-5 sm:px-7">
          <DialogHeader>
            <div className="mb-2 flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary"><Copy className="h-4 w-4" /></span>
              <Badge variant="outline" className="text-[10px] uppercase tracking-[0.12em]">New paused campaign</Badge>
            </div>
            <DialogTitle className="text-xl tracking-tight">Duplicate selected winners</DialogTitle>
            <DialogDescription className="max-w-2xl leading-relaxed">
              Reuse the source creative or post where eligible. This is a fresh source check for {brandName}; the original ads stay untouched.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="space-y-6 px-5 py-5 sm:px-7">
          {preflightLoading && (
            <div aria-label="Loading selected ad details" className="space-y-3">
              <div className="h-4 w-52 animate-pulse rounded bg-muted" />
              <div className="h-24 animate-pulse rounded-lg border bg-muted/50" />
              <div className="h-24 animate-pulse rounded-lg border bg-muted/50" />
            </div>
          )}
          {preflightError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Could not verify selected ads</AlertTitle>
              <AlertDescription className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <span>{preflightError}</span>
                <Button size="sm" variant="outline" onClick={refreshPreflight}>Retry source check</Button>
              </AlertDescription>
            </Alert>
          )}

          {!!rows.length && (
            <section className="space-y-3">
              <div className="flex flex-wrap items-end justify-between gap-2">
                <div>
                  <h3 className="text-sm font-semibold">Source ads</h3>
                  <p className="text-xs text-muted-foreground">{rows.length} selected in account {accountId}. Spend is from the current report; source metadata is freshly checked.</p>
                </div>
                 {!!blockedIds.length && <Button variant="outline" size="sm" disabled={inputsLocked} onClick={() => onRemoveBlocked(blockedIds)}>Remove blocked ads from selection</Button>}
              </div>
              <div className="rounded-lg border">
                <Table containerClassName="max-h-64">
                  <TableHeader className="sticky top-0 z-10 bg-muted/90">
                    <TableRow>
                      <TableHead>Selected ad</TableHead>
                      <TableHead>Source campaign / ad set</TableHead>
                      <TableHead className="text-right">Displayed spend</TableHead>
                      <TableHead>Reuse eligibility</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map(({ identity, source }) => (
                      <TableRow key={identity.adId}>
                         <TableCell className="min-w-40 font-medium">{identity.name || source?.name || identity.adId}</TableCell>
                        <TableCell className="min-w-40 text-xs text-muted-foreground">
                          <span className="block text-foreground/90">{identity.source.campaignName || "Campaign unavailable"}</span>
                          <span>{identity.source.adsetName || "Ad set unavailable"}</span>
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-right tabular-nums">{money(source?.metrics?.spend, currency)}</TableCell>
                        <TableCell>
                          {identity.proofMode === "blocked" ? (
                            <div className="space-y-1">
                              <Badge variant="destructive">Blocked</Badge>
                              <p className="max-w-52 text-xs text-destructive">{identity.blockedReason || "This source cannot be duplicated safely."}</p>
                            </div>
                          ) : (
                            <div className="space-y-1">
                              <Badge variant="secondary" className="gap-1"><ShieldCheck className="h-3 w-3" />Eligible</Badge>
                              <p className="text-xs text-muted-foreground">{identity.proofMode === "post_reuse" ? "Existing post reuse eligible" : "Creative reuse eligible"}</p>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <p className="text-xs leading-relaxed text-muted-foreground">
                Eligibility is based on fresh reporting evidence, not post-launch verification. Reusing an existing post may carry its likes, comments and shares; tracking and attribution are not guaranteed.
              </p>
            </section>
          )}
          {admission.error && <Alert variant="destructive"><AlertDescription>{admission.error}</AlertDescription></Alert>}
          {!!rows.length && !currencyMatches && <Alert variant="destructive"><AlertDescription>
            {currencyWarning}
          </AlertDescription></Alert>}

          {!!preflight && !blockedIds.length && (
            <>
              <section className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="duplicate-campaign-name">New campaign name</Label>
                  <Input id="duplicate-campaign-name" value={campaignName} maxLength={255} disabled={inputsLocked} onChange={event => setCampaignName(event.target.value)} />
                </div>
                <fieldset className="space-y-2">
                  <Label>Budget ownership</Label>
                  <RadioGroup value={budgetMode} onValueChange={value => {
                    const next = value as "abo" | "cbo";
                    if (next === "cbo" && autoPrefilledValue.current && dailyBudget === autoPrefilledValue.current) {
                      setDailyBudget("");
                      autoPrefilledValue.current = null;
                    }
                    setBudgetMode(next);
                  }} className="grid grid-cols-2 gap-2" disabled={inputsLocked}>
                    <Label htmlFor="duplicate-budget-abo" className="flex cursor-pointer items-start gap-2 rounded-lg border p-3 has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                      <RadioGroupItem id="duplicate-budget-abo" value="abo" disabled={inputsLocked} />
                       <span><span className="block text-sm font-semibold">ABO</span><span className="block text-xs font-normal text-muted-foreground">Budget per ad set, recommended for isolated testing</span></span>
                    </Label>
                    <Label htmlFor="duplicate-budget-cbo" className="flex cursor-pointer items-start gap-2 rounded-lg border p-3 has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                      <RadioGroupItem id="duplicate-budget-cbo" value="cbo" disabled={inputsLocked} />
                      <span><span className="block text-sm font-semibold">CBO</span><span className="block text-xs font-normal text-muted-foreground">Budget at campaign</span></span>
                    </Label>
                  </RadioGroup>
                </fieldset>
                <fieldset className="space-y-2">
                  <Label>Ad set structure</Label>
                  <RadioGroup value={structure} onValueChange={value => setStructure(value as "one_adset_per_ad" | "single_adset")} className="space-y-2" disabled={inputsLocked}>
                    <Label htmlFor="duplicate-one-adset" className="flex cursor-pointer items-start gap-2 rounded-lg border p-3 has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                      <RadioGroupItem id="duplicate-one-adset" value="one_adset_per_ad" disabled={inputsLocked} />
                       <span><span className="block text-sm font-semibold">One ad set per ad</span><span className="block text-xs font-normal text-muted-foreground">Isolated test. Keep each ad's source ad set as its targeting reference.</span></span>
                    </Label>
                    <Label htmlFor="duplicate-single-adset" className="flex cursor-pointer items-start gap-2 rounded-lg border p-3 has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                      <RadioGroupItem id="duplicate-single-adset" value="single_adset" disabled={inputsLocked} />
                      <span><span className="block text-sm font-semibold">Single shared ad set</span><span className="block text-xs font-normal text-muted-foreground">Targeting comes from {firstSource?.source.adsetName || "the first selected source ad set"} ({firstSource?.adsetId || firstSource?.adId || "source unavailable"}).</span></span>
                    </Label>
                  </RadioGroup>
                </fieldset>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="duplicate-budget">Daily budget per {budgetMode === "abo" ? "ad set" : "campaign"}{currency ? ` (${currency})` : ""}</Label>
                  <Input
                    id="duplicate-budget"
                    inputMode="decimal"
                    placeholder={currencyMatches && prefill.value ? prefill.value : "Enter a daily budget"}
                    value={dailyBudget}
                    disabled={inputsLocked}
                    aria-invalid={budgetInvalid}
                    onChange={event => {
                      const value = event.target.value;
                      if (value !== autoPrefilledValue.current) autoPrefilledValue.current = null;
                      setDailyBudget(value);
                    }}
                  />
                  <p className="text-xs text-muted-foreground">
                    {dailyBudget ? (budgetMinor !== null ? `Exact amount: ${formatMinorBudget(budgetMinor, currency || "USD")}` : "Enter a positive amount using the account currency's decimal precision.") : reason || "Enter a positive daily budget."}
                  </p>
                  {!currency && <p className="text-xs text-destructive">Account currency is unavailable. A valid currency is required to parse the budget.</p>}
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="duplicate-suffix">Copied ad name suffix <span className="font-normal text-muted-foreground">Optional</span></Label>
                  <Input id="duplicate-suffix" value={suffix} maxLength={100} disabled={inputsLocked} onChange={event => setSuffix(event.target.value)} />
                </div>
              </section>
               {(launchError || frozenAmbiguous) && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>{frozenAmbiguous ? "Admission needs confirmation" : "Duplication was not admitted"}</AlertTitle>
                  <AlertDescription className="space-y-2">
                     <p>{launchError ?? "A prior admission still needs confirmation."}</p>
                    {frozenAmbiguous && <p>Details are locked so a retry uses the same request key and payload. Do not start a replacement run until this attempt is checked.</p>}
                  </AlertDescription>
                </Alert>
              )}
              <Alert className="border-primary/20 bg-primary/[0.035]">
                <Check className="h-4 w-4 text-primary" />
                <AlertTitle>Safety and delivery</AlertTitle>
                <AlertDescription>
                   Will create 1 paused campaign, {structure === "single_adset" ? 1 : rows.length} paused ad sets and {rows.length} paused ads. Source ads remain unchanged. Nothing spends until you enable the new objects. Likes, comments and shares are intended to carry over through original creative or post reuse; confirmation requires later verification.
                </AlertDescription>
              </Alert>
            </>
          )}
        </div>

        <DialogFooter className="border-t bg-muted/20 px-5 py-4 sm:px-7">
          <Button variant="outline" onClick={onClose} disabled={launching}>Cancel</Button>
          <Button onClick={submit} disabled={!canSubmit || launching}>
            {launching && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {frozenAmbiguous ? "Retry same admission" : launching ? "Submitting…" : `Create paused campaign${rows.length ? ` · ${rows.length} ads` : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default DuplicateAdsDialog;
